import { useEffect, useMemo } from 'react';
import type {
  Account,
  MemberSkill,
  OauthConnection,
  Skill,
  SkillLevel,
  TrackerConnection,
} from '@forecastify/shared';
import { Select, type IconName } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { AccountSkillsSection } from './skills-section';
import { clearAccountDirty, setAccountDirty } from './account-dirty';
import { ProfileSection } from './profile-section';
import { SecuritySection, type SecurityHandlers } from './security-section';
import { PreferencesSection } from './preferences-section';
import { ThemeSection } from './theme-section';
import { NotificationsSection } from './notifications-section';
import { ConnectResult } from './connect-result';
import type { ConnectReturnRead } from '../integrations/connect-return';
import {
  IntegrationsSection,
  type ApplicationHandlers,
  type TokenHandlers,
  type GoogleConnection,
  type GoogleHandlers,
} from './integrations-section';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Fiókom — seven sections behind a 216px sidebar.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 462–952 for the logic, `markup.html` for the drawing. Measured out of the markup
 * rather than eyeballed: `w-[216px]` nav, `md:flex-row`, cards at `max-w-[680px]` with
 * `rounded-xl border p-4 shadow-sm`, 13px card titles, 12px hints, 11px rules.
 *
 * ⚠️ **The nav collapses to a `Select` below `md`, which is the mockup's own answer** — not a
 * drawer. A seven-item list is short enough that a native picker beats a slide-over, and the markup
 * draws exactly that (`md:hidden` with `usNavOptions`).
 *
 * ⚠️ **The dirty dot is per SECTION, and that is load-bearing.** `usSectionDirty` marks the tab, so
 * somebody who edits their profile, wanders to Notifications and comes back can see where the
 * unsaved change is. A single global "unsaved" flag would tell them there is one and not where.
 *
 * ⚠️ **What is DIFFERENT from the mockup, in one place:**
 *
 *  1. **A job-title field exists.** `usSetTitle` is in the mockup's logic and drawn by nothing —
 *     one of 24 such keys (`docs/plan/mockup-findings.md`). `chunks.md` says build the UI.
 *  2. **Personal access tokens, email change and data export have UI**, for the same reason: 13, 5
 *     and 2 keys respectively, all undrawn.
 *  3. **The theme gallery derives its swatches from the token layer** rather than carrying forty
 *     hex literals like the mockup's `pv` maps. `debt.md` has wanted that since C1.
 *  4. **Passkeys are real**, verified by our own WebAuthn implementation — the mockup's are a mock
 *     service returning `{ ok: true }`.
 */

/** ⚠️ The values, so a `?section=` from outside can be checked rather than trusted. */
export const ACCOUNT_SECTIONS = [
  'profile',
  /* ⚠️ C54 · second, right after Profil — the mockup's own `navDef` order. */
  'skills',
  'security',
  'preferences',
  'theme',
  'notifications',
  'integrations',
  /*
   * ⚠️⚠️ **THE `account` SECTION IS GONE — item 19.** It held two things: a personal data export
   * and *delete my account*. The product owner's ruling of 9 Sep 2026 removes the subpage.
   *
   * ⚠️ **What goes with it, stated plainly:** self-service data portability and self-service
   * erasure. The WORKSPACE export at `/settings/data` is untouched and is a different thing — it
   * exports a workspace's records for an administrator, not one person's own for themselves.
   * `account.service.ts` still serves both endpoints; only the screen is gone, so restoring the
   * tab is a UI change rather than a rebuild.
   */
] as const;

export type AccountSectionId = (typeof ACCOUNT_SECTIONS)[number];

/**
 * The mockup's own order, labels and glyphs (`navDef`).
 *
 * ⚠️ Exported since 13 Oct 2026: the app's sidebar draws these in settings mode (`app.tsx`), so this
 * screen no longer draws a nav of its own at desk width.
 */
export const ACCOUNT_NAV: readonly { id: AccountSectionId; label: MessageKey; icon: IconName }[] = [
  { id: 'profile', label: 'account.section.profile', icon: 'user-round' },
  { id: 'skills', label: 'account.section.skills', icon: 'sparkles' },
  { id: 'security', label: 'account.section.security', icon: 'shield-check' },
  { id: 'preferences', label: 'account.section.preferences', icon: 'sliders-horizontal' },
  /* ⚠️ `palette` is the 78th glyph — an addition, declared in `ICON_ADDITIONS` and flagged in
   * `decisions.md`. The design system's 77 do not include one, and substituting a different icon
   * would diverge visibly from a screen the mockup does draw. */
  { id: 'theme', label: 'account.section.theme', icon: 'palette' },
  { id: 'notifications', label: 'account.section.notifications', icon: 'bell' },
  { id: 'integrations', label: 'account.section.integrations', icon: 'plug' },
];

export interface AccountScreenProps {
  /** C37 · what this person could connect their own tracker account to. Null until it lands. */
  /** Which pane is open — the `?section=` the sidebar's rows link to. */
  readonly section: AccountSectionId;
  /** The narrow-screen picker's change — the sidebar's rows navigate by address instead. */
  readonly onSectionChange: (section: AccountSectionId) => void;
  readonly connections: readonly TrackerConnection[] | null;
  /**
   * 26 Oct 2026 · **back from Atlassian** — while set, the pane shows what came of it in place of
   * any section, until `onConnectResultDone`.
   */
  readonly connectResult?: ConnectReturnRead | null;
  readonly onConnectResultDone?: () => void;
  /** ⚠️ C52 · the applications this person approved. Null until it lands. */
  readonly applications: readonly OauthConnection[] | null;
  /** 22 Oct 2026 · the Google account connected to this one; null while it loads. */
  readonly google?: GoogleConnection | null;
  readonly googleHandlers?: GoogleHandlers;
  readonly googleBusy?: boolean;
  readonly busy: boolean;
  readonly onConnect: (instanceId: string) => void;
  readonly onDisconnect: (remoteSiteId: string) => void;
  /** `null` while loading — the screen renders its own skeleton rather than a blank pane. */
  readonly account: Account | null;
  /**
   * C54 · ⚠️ **All three lists, because the row draws all three.** A skill's category colour,
   * the level's name and every level's description are on the screen at once; loading them
   * separately per row would be one request per skill.
   */
  readonly mySkills?: readonly MemberSkill[];
  /** Until the skills' first read lands: the list draws skeleton rows and no count. */
  readonly skillsLoading?: boolean;
  readonly skillLevels?: readonly SkillLevel[];
  readonly skillVocabulary?: readonly Skill[];
  readonly skillError?: string | null;
  /** ⚠️ One handler for add and for change-the-level — the server has one row either way. */
  readonly onSetMySkill?: (skillId: string, levelId: string) => void;
  readonly onRemoveMySkill?: (skillId: string) => void;
  /** C22 · re-reads the account after a profile picture changes. */
  readonly onReload?: () => Promise<void>;
  readonly onSaveProfile?: (patch: {
    /* ⚠️ Optional since the form stopped asking — `0101`’s trigger composes it. */
    displayName?: string;
    givenName?: string;
    familyName?: string;
    phone: string | null;
    version: number;
  }) => Promise<void>;
  readonly onSavePreferences?: (patch: {
    locale: 'hu' | 'en';
    timezone: string;
    dateFormat: string;
    weekStart: string;
    theme: string;
    version: number;
  }) => Promise<void>;
  readonly onSaveNotifications?: (patch: unknown) => Promise<void>;
  readonly onChangeEmail?: (email: string) => Promise<void>;
  readonly security?: SecurityHandlers;
  readonly tokens?: TokenHandlers;
  /** ⚠️ C52 · named apart from the `applications` LIST above: one is data, one is what to do. */
  readonly applicationHandlers?: ApplicationHandlers;
  /** So the Theme tab can paint immediately without waiting for a round trip. */
  readonly onPreviewTheme?: (theme: string) => void;
}

export function AccountScreen({
  account,
  onReload,
  onSaveProfile,
  onSavePreferences,
  onSaveNotifications,
  onChangeEmail,
  security,
  tokens,
  onPreviewTheme,
  section,
  onSectionChange,
  connections,
  connectResult = null,
  onConnectResultDone,
  applications,
  applicationHandlers,
  google = null,
  googleHandlers,
  googleBusy = false,
  busy,
  onConnect,
  onDisconnect,
  mySkills,
  skillsLoading,
  skillLevels,
  skillVocabulary,
  skillError,
  onSetMySkill,
  onRemoveMySkill,
}: AccountScreenProps) {
  const t = useT();

  /**
   * ⚠️ **Which sections have unsaved work, tracked by the SECTIONS themselves.**
   *
   * Each one owns its draft — the screen cannot know whether a phone number differs from the stored
   * one without duplicating every section's comparison. So a section reports upwards, and this map
   * is only ever read to draw a dot. The alternative, hoisting every draft here, is how a settings
   * screen becomes one component with fourteen `useState`s and no clear owner.
   */
  const markDirty = useMemo(
    () => (id: AccountSectionId, value: boolean) => {
      setAccountDirty(id, value);
    },
    [],
  );
  /* ⚠️ A dot must not outlive the drafts it described — `account-dirty.ts`. */
  useEffect(() => clearAccountDirty, []);

  const navOptions = ACCOUNT_NAV.map((entry) => ({ value: entry.id, label: t(entry.label) }));

  return (
    <div data-screen="account" className="flex min-h-0 flex-1 flex-col md:flex-row">
      {/* ⚠️ No nav of its own at desk width since 13 Oct 2026 — the app's sidebar IS this
          screen's menu in settings mode, with the unsaved dots (`account-dirty.ts`). */}

      {/* ── the same nav as a native picker, below md — the mockup's own answer ──────────── */}
      <div className="flex items-center gap-2 border-b p-2 desktop:hidden">
        <Select
          aria-label={t('nav.account')}
          value={section}
          options={navOptions}
          className="w-full"
          onChange={(event) => {
            onSectionChange(event.target.value as AccountSectionId);
          }}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {connectResult !== null ? (
          <ConnectResult
            result={connectResult}
            connections={connections}
            busy={busy}
            onRetry={onConnect}
            onDone={() => {
              onConnectResultDone?.();
            }}
          />
        ) : account === null ? (
          /* ⚠️ A per-section skeleton would be seven skeletons; one line is honest and the design
             system forbids shimmer anyway — the layout must not reflow on arrival. */
          <p role="status" className="text-muted-foreground text-[13px]">
            {t('state.loading')}
          </p>
        ) : (
          <>
            {section === 'profile' && (
              <ProfileSection
                profile={account.profile}
                onSave={onSaveProfile}
                onChangeEmail={onChangeEmail}
                onReload={onReload}
                onDirtyChange={(value) => {
                  markDirty('profile', value);
                }}
              />
            )}
            {section === 'skills' && (
              <AccountSkillsSection
                skills={mySkills ?? []}
                loading={skillsLoading}
                levels={skillLevels ?? []}
                vocabulary={skillVocabulary ?? []}
                serverError={skillError ?? null}
                onSetLevel={(skillId, levelId) => {
                  onSetMySkill?.(skillId, levelId);
                }}
                onAdd={(skillId, levelId) => {
                  onSetMySkill?.(skillId, levelId);
                }}
                onRemove={(skillId) => {
                  onRemoveMySkill?.(skillId);
                }}
              />
            )}
            {section === 'security' && <SecuritySection handlers={security} />}
            {section === 'preferences' && (
              <PreferencesSection
                preferences={account.preferences}
                onSave={onSavePreferences}
                onDirtyChange={(value) => {
                  markDirty('preferences', value);
                }}
              />
            )}
            {section === 'theme' && (
              <ThemeSection
                preferences={account.preferences}
                onSave={onSavePreferences}
                onPreview={onPreviewTheme}
              />
            )}
            {section === 'notifications' && (
              <NotificationsSection
                notifications={account.notifications}
                onSave={onSaveNotifications}
                onDirtyChange={(value) => {
                  markDirty('notifications', value);
                }}
              />
            )}
            {section === 'integrations' && (
              <IntegrationsSection
                tokens={account.tokens}
                handlers={tokens}
                connections={connections}
                applications={applications}
                applicationHandlers={applicationHandlers}
                google={google}
                {...(googleHandlers === undefined ? {} : { googleHandlers })}
                googleBusy={googleBusy}
                busy={busy}
                onConnect={onConnect}
                onDisconnect={onDisconnect}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
